import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { makeStyles } from "tss-react/mui";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CodeBlock } from "component/shared/code-block";
import { WebsitePlatformType } from "type/website-platform";
import * as kestrelEmbedCode from "shared/kestrel-embed-code";
import { CONTACT_SUPPORT_URL } from "../constants";
import { styles } from "./style";

/**
 * Building blocks for the first-time IDX setup wizard (MAX-4419 task 6), which walks a client
 * through creating a page at a fixed URL and pasting the page embed code into it. `PageEmbedCode`
 * is also the embed block of the Set Up / Edit page modal (MAX-4533).
 */

// One sheet for the six building blocks below, so it stays a hook rather than a withStyles per block.
const useStyles = makeStyles()(styles);

/** Body copy at the wizard's Web/Body1 size. */
export const SetupText: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { classes } = useStyles();
	return <Mui.Typography className={classes.text}>{children}</Mui.Typography>;
};

/** The yellow warning callout the setup steps open with. */
export const SetupCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { classes } = useStyles();
	return (
		<div className={classes.callout} role="note">
			<Icons.AlertTriangle size={16} className={classes.calloutIcon} />
			<Mui.Typography className={classes.text}>{children}</Mui.Typography>
		</div>
	);
};

/** "contact support ↗", an external link to the support contact page. */
export const ContactSupportLink: React.FC = () => {
	const { classes } = useStyles();
	return (
		<Mui.Link href={CONTACT_SUPPORT_URL} target="_blank" rel="noopener noreferrer" className={classes.link}>
			contact support
			<Icons.ExternalLink size={16} />
		</Mui.Link>
	);
};

/** A read-only value with its label and a Copy button: a page URL or the registration key. */
export const CopyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => {
	const { classes } = useStyles();
	const [copied, setCopied] = useState(false);
	return (
		<div className={classes.copyValue}>
			<Mui.Typography className={classes.copyLabel}>{label}</Mui.Typography>
			<div className={classes.copyRow}>
				<Mui.Typography className={classes.copyValueText}>{value}</Mui.Typography>
				<CopyToClipboard text={value} onCopy={() => setCopied(true)}>
					<Mui.Tooltip title={copied ? "Copied!" : ""} onClose={() => setCopied(false)}>
						<Mui.Button
							variant="text"
							color="primary"
							className={classes.copyButton}
							startIcon={<Icons.Copy size={16} />}
							aria-label={`Copy ${label}`}
						>
							Copy
						</Mui.Button>
					</Mui.Tooltip>
				</CopyToClipboard>
			</div>
		</div>
	);
};

/** The page embed code, in the Wix text variant for Wix. */
export const PageEmbedCode: React.FC<{ websitePlatform: WebsitePlatformType }> = ({ websitePlatform }) => (
	<CodeBlock
		refreshed
		code={kestrelEmbedCode.page(websitePlatform)}
		language={websitePlatform.textEmbedCode ? "text" : "html"}
	/>
);

interface PageSetupInstructionsProps {
	/** The page's name as the client knows it, e.g. "Search". */
	pageLabel: string;
	/** The full, fixed URL the page must live at. */
	url: string;
	websitePlatform: WebsitePlatformType;
}

/**
 * "To create the Search page: 1. Create a page at <URL>. 2. Paste this code into its body."
 * Task 6's Set Up Your IDX Pages step (VPLAT 24).
 */
export const PageSetupInstructions: React.FC<PageSetupInstructionsProps> = ({ pageLabel, url, websitePlatform }) => {
	const { classes } = useStyles();
	return (
		<>
			<SetupText>To create the {pageLabel} page:</SetupText>
			<ol className={classes.list}>
				<li>
					<SetupText>Create a page in your website builder at:</SetupText>
					<CopyValue label={`${pageLabel} URL`} value={url} />
				</li>
				<li>
					<SetupText>Then, paste this code into the body of the page:</SetupText>
					<PageEmbedCode websitePlatform={websitePlatform} />
				</li>
			</ol>
		</>
	);
};
